import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QuoteRequest } from '@/hooks/queries/useQuoteRequest';

interface PaginatorPayload<T> {
  data: T[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

interface NormalizedPaginated<T> {
  data: T[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export function useAdminQuoteRequests(params: { status?: string; page?: number } = {}) {
  const searchParams = new URLSearchParams();
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);
  if (params.page) searchParams.set('page', params.page.toString());
  const qs = searchParams.toString();

  return useQuery({
    queryKey: ['admin', 'quote-requests', params],
    queryFn: async () => {
      const response = await api.get<PaginatorPayload<QuoteRequest>>(
        `/admin/quote-requests${qs ? `?${qs}` : ''}`,
      );
      const payload = response.data;

      return {
        data: Array.isArray(payload?.data) ? payload.data : [],
        meta: {
          current_page: payload?.current_page ?? 1,
          last_page: payload?.last_page ?? 1,
          per_page: payload?.per_page ?? 15,
          total: payload?.total ?? 0,
        },
      } satisfies NormalizedPaginated<QuoteRequest>;
    },
  });
}

export function useAdminQuoteRequest(id: string) {
  return useQuery({
    queryKey: ['admin', 'quote-requests', id],
    queryFn: async () => {
      const response = await api.get<QuoteRequest>(`/admin/quote-requests/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
}

export function useAdminQuotePendingCount() {
  return useQuery({
    queryKey: ['admin', 'quote-requests', 'pending-count'],
    queryFn: async () => {
      const response = await api.get<{ count: number }>('/admin/quote-requests/pending-count');
      return response.data.count;
    },
    refetchInterval: 60_000,
  });
}
